import {
  BLACKOUT_CATEGORY,
  BUNDLED_EXPERIENCES_CATEGORY,
  CAMPAIGN_CATEGORY,
  DATES_RESTRICTIONS_CATEGORY,
  DEAL_OPTIONS_INCLUSIONS_CATEGORY,
  EGS_CATEGORY,
  SETUP_CATEGORY,
  TAXES_PAYMENTS_CATEGORY,
} from './checklistCategories';
import type { BlackoutChecklistRow } from './tabs/blackoutDates/types';
import type { BundledExperienceChecklistRow } from './tabs/bundledExperiences/types';
import type { CampaignChecklistRow } from './tabs/campaign/types';
import type { DatesRestrictionsChecklistRow } from './tabs/datesRestrictions/types';
import type { DealOptionsInclusionsChecklistRow } from './tabs/dealOptionsInclusions/types';
import type { EgsCapacityChecklistRow, EgsMarginChecklistRow } from './tabs/egs/types';
import type { SetupChecklistRow } from './tabs/setup/types';
import type { TaxesPaymentsChecklistRow } from './tabs/taxesPayments/types';
import type { ApprovalReportSummary, ChecklistRow, ManualApprovalState } from './types';

export function filterRows<Row>({
  isApproved,
  rows,
  searchQuery,
  showAutoApproved,
  text,
}: {
  isApproved: (row: Row) => boolean;
  rows: Array<Row>;
  searchQuery: string;
  showAutoApproved: boolean;
  text: (row: Row) => string;
}) {
  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  return rows.filter((row) => {
    if (!showAutoApproved && isApproved(row)) {
      return false;
    }

    return !normalizedSearchQuery || text(row).toLowerCase().includes(normalizedSearchQuery);
  });
}

export function buildReportSummaries({
  blackoutRows,
  bundledExperienceRows,
  taxesPaymentsRows,
  capacityRows,
  campaignRows,
  datesRestrictionsRows,
  dealOptionsInclusionsRows,
  manualApprovals,
  marginRows,
  offerId,
  propertyId,
  setupRows,
  vendorId,
}: {
  blackoutRows: Array<BlackoutChecklistRow>;
  bundledExperienceRows: Array<BundledExperienceChecklistRow>;
  taxesPaymentsRows: Array<TaxesPaymentsChecklistRow>;
  capacityRows: Array<EgsCapacityChecklistRow>;
  campaignRows: Array<CampaignChecklistRow>;
  datesRestrictionsRows: Array<DatesRestrictionsChecklistRow>;
  dealOptionsInclusionsRows: Array<DealOptionsInclusionsChecklistRow>;
  manualApprovals: ManualApprovalState;
  marginRows: Array<EgsMarginChecklistRow>;
  offerId: string;
  propertyId?: string;
  setupRows: Array<SetupChecklistRow>;
  vendorId?: string;
}): Array<ApprovalReportSummary> {
  const summaries: Array<ApprovalReportSummary> = [];

  const setupRowsRequiringApproval = setupRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (setupRowsRequiringApproval.length > 0) {
    const setupRowCopy = `setup row${setupRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const setupReviewVerb = setupRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: SETUP_CATEGORY.id,
      categoryLabel: SETUP_CATEGORY.label,
      count: setupRowsRequiringApproval.length,
      description: `${setupRowsRequiringApproval.length} ${setupRowCopy} still ${setupReviewVerb} review. Verify setup evidence before approval.`,
      sourceLinks: [],
    });
  }

  const campaignRowsRequiringApproval = campaignRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (campaignRowsRequiringApproval.length > 0) {
    const campaignRowCopy = `Campaign row${campaignRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const campaignReviewVerb = campaignRowsRequiringApproval.length === 1 ? 'needs' : 'need';

    summaries.push({
      categoryId: CAMPAIGN_CATEGORY.id,
      categoryLabel: CAMPAIGN_CATEGORY.label,
      count: campaignRowsRequiringApproval.length,
      description: `${campaignRowsRequiringApproval.length} ${campaignRowCopy} still ${campaignReviewVerb} review. Verify source values in offer details, opportunity details, and the Policy Bible.`,
      sourceLinks: [
        {
          href: 'https://aussiecommerce.atlassian.net/wiki/spaces/SO/pages/2335834126/Contracting+and+Assessment+Information+Chains+Hotel+Specific',
          label: 'Open Policy Bible',
        },
      ],
    });
  }

  const datesRestrictionsRowsRequiringApproval = datesRestrictionsRows.filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  if (datesRestrictionsRowsRequiringApproval.length > 0) {
    const rowCopy = `Dates & Restrictions row${datesRestrictionsRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const reviewVerb = datesRestrictionsRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: DATES_RESTRICTIONS_CATEGORY.id,
      categoryLabel: DATES_RESTRICTIONS_CATEGORY.label,
      count: datesRestrictionsRowsRequiringApproval.length,
      description: `${datesRestrictionsRowsRequiringApproval.length} ${rowCopy} still ${reviewVerb} review. Verify deal-option setup and synced Salesforce values.`,
      sourceLinks: [],
    });
  }

  const blackoutRowsRequiringApproval = blackoutRows.filter((row) => !isRowApproved(row, manualApprovals));
  const blackoutReviewCount = blackoutRowsRequiringApproval.length;
  if (blackoutReviewCount > 0) {
    const blackoutRuleCopy = `blackout rule${blackoutReviewCount === 1 ? '' : 's'}`;
    const blackoutReviewVerb = blackoutReviewCount === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: BLACKOUT_CATEGORY.id,
      categoryLabel: BLACKOUT_CATEGORY.label,
      count: blackoutReviewCount,
      description: `${blackoutReviewCount} ${blackoutRuleCopy} still ${blackoutReviewVerb} review. Verify blackout dates against travel validity and gala/festive dates.`,
      sourceLinks: [],
    });
  }

  const egsRowsRequiringApproval = [...marginRows, ...capacityRows].filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  if (egsRowsRequiringApproval.length > 0) {
    const egsRowCopy = `EGS row${egsRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const egsReviewVerb = egsRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: EGS_CATEGORY.id,
      categoryLabel: EGS_CATEGORY.label,
      count: egsRowsRequiringApproval.length,
      description: `${egsRowsRequiringApproval.length} ${egsRowCopy} still ${egsReviewVerb} review. Verify source data in AP room-rate capacity and surcharge setup.`,
      sourceLinks: buildEgsSourceLinks({ capacityRows, propertyId, vendorId }),
    });
  }

  const dealRowsRequiringApproval = dealOptionsInclusionsRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (dealRowsRequiringApproval.length > 0) {
    const copy = `inclusion${dealRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const verb = dealRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: DEAL_OPTIONS_INCLUSIONS_CATEGORY.id,
      categoryLabel: DEAL_OPTIONS_INCLUSIONS_CATEGORY.label,
      count: dealRowsRequiringApproval.length,
      description: `${dealRowsRequiringApproval.length} ${copy} still ${verb} review. Verify Inclusion Asset Value matches Deal Option Inclusion RRP in Salesforce.`,
      sourceLinks: [],
    });
  }

  const bundledExperienceRowsRequiringApproval = bundledExperienceRows.filter(
    (row) => !isRowApproved(row, manualApprovals),
  );
  if (bundledExperienceRowsRequiringApproval.length > 0) {
    const rowCopy = `bundled experience row${bundledExperienceRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const reviewVerb = bundledExperienceRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: BUNDLED_EXPERIENCES_CATEGORY.id,
      categoryLabel: BUNDLED_EXPERIENCES_CATEGORY.label,
      count: bundledExperienceRowsRequiringApproval.length,
      description: `${bundledExperienceRowsRequiringApproval.length} ${rowCopy} still ${reviewVerb} review. Verify synced deal-option and opportunity evidence in Salesforce.`,
      sourceLinks: [{ href: `/opportunity/${offerId}`, label: 'Verify source data' }],
    });
  }

  const taxesRowsRequiringApproval = taxesPaymentsRows.filter((row) => !isRowApproved(row, manualApprovals));
  if (taxesRowsRequiringApproval.length > 0) {
    const taxesRowCopy = `rule${taxesRowsRequiringApproval.length === 1 ? '' : 's'}`;
    const taxesReviewVerb = taxesRowsRequiringApproval.length === 1 ? 'needs' : 'need';
    summaries.push({
      categoryId: TAXES_PAYMENTS_CATEGORY.id,
      categoryLabel: TAXES_PAYMENTS_CATEGORY.label,
      count: taxesRowsRequiringApproval.length,
      description: `${taxesRowsRequiringApproval.length} Taxes & Payments ${taxesRowCopy} still ${taxesReviewVerb} review. Verify source data in the Salesforce opportunity contract and Admin Portal property taxes.`,
      sourceLinks: [],
    });
  }

  return summaries;
}

export function isRowApproved(row: ChecklistRow, manualApprovals: ManualApprovalState) {
  return row.status === 'approved' || !!manualApprovals[row.id];
}

function buildEgsSourceLinks({
  capacityRows,
  propertyId,
  vendorId,
}: {
  capacityRows: Array<EgsCapacityChecklistRow>;
  propertyId?: string;
  vendorId?: string;
}) {
  if (!propertyId || !vendorId) {
    return [];
  }

  const sourceRow = capacityRows.find((row) => row.roomTypeId && row.roomRateId);

  if (!sourceRow?.roomTypeId || !sourceRow.roomRateId) {
    return [];
  }

  return [
    {
      href: `/vendors/${vendorId}/properties/${propertyId}/room-types/${sourceRow.roomTypeId}/room-rates/${sourceRow.roomRateId}`,
      label: 'Verify source data',
    },
  ];
}
